import { IPlayerApiService } from "services/models/ApiService";
import { v4 as uuidv4 } from "uuid";
import { delay, IAbortablePromise, abortableDelay, randomErrorOccurence } from "utils";
import { getRandom, getRandomArray, mockPeriods, mockTripsForPeriods } from "./data";
import { mockHotelStore } from "./hotelbooking-data";
import { mockPlayerDataStore } from "./player-data";
import { ICreateNote } from "services/models/Notes/ICreateNote";
import {
  IBasicPlayerInformation,
  IBasicPlayerNote,
  IBasicReservation,
  IHotelBookingCalendar,
  IHotelBookingGuest,
  IHotelBookingPayment,
  IHotelBookingSummary,
  IHotelRoomBookings,
  IPlayerComp,
  IPlayerCreditTransaction,
  IPlayerInformation,
  IPlayerNote,
  IPlayerOffer,
  IPlayerSearchInfo,
  IPlayerSourceOfFunds,
  IPlayerStatsForTimePeriod,
  IPlayerTrip,
  IPlayerTripHistory,
  IReservation,
  IShift4Info,
} from "services/models";
import { mockReservations } from "./reservations-data";
import { ApiError } from "services/api/misc";
import { TaskStatus } from "services/models/TaskStatus";
import { IPlayerFolio } from "services/models/Player/PlayerFolio";
import { IClubSerranoInfo, IPlayerMobileAppInformation } from "services/models/Player/PlayerMobileAppInformation";

export class MockPlayerApiService implements IPlayerApiService {
  getFolios(playerId: string | number, confirmationNumber: string | number): Promise<IPlayerFolio[]> {
    return delay(() => {
      const folios = mockPlayerDataStore.folios;
      randomErrorOccurence();
      return folios;
    });
  }
  search(term: string | undefined): Promise<IPlayerSearchInfo[]> {
    return delay(() => {
      return [];
    });
  }

  getBasicPlayerInformation(id: string): Promise<IBasicPlayerInformation> {
    return delay(() => {
      return mockPlayerDataStore.getBasicPlayerInformation(id);
    });
  }

  getPlayerInformation(id: string): Promise<IPlayerInformation> {
    return delay(() => {
      return mockPlayerDataStore.getPlayerInformation(id);
    });
  }

  getStatsForTimePeriod(period: string): IAbortablePromise<IPlayerStatsForTimePeriod> {
    return abortableDelay(() => {
      return mockPeriods[period] ?? mockPeriods["7"];
    });
  }

  getTripHistoryForTimePeriod(period: string): IAbortablePromise<IPlayerTripHistory[]> {
    return abortableDelay(() => {
      return mockTripsForPeriods[period] ?? mockTripsForPeriods["7"];
    });
  }

  getHotelBookingCalendar(playerId: string): Promise<IHotelBookingCalendar> {
    return delay(() => {
      return mockHotelStore.calendar;
    });
  }

  getHotelPrimaryGuest(playerId: string, bookingId: string): Promise<IHotelBookingGuest> {
    return delay(() => {
      return {
        firstName: "Nicholas",
        lastName: "Mata",
        email: "nicholas.mata@sanmanuel.com",
        phoneNumber: null,
        clubNumber: 2728174,
        guestId: "ad199907-bbca-4ce0-a14c-7537362b5f53",
        primary: true,
      };
    });
  }

  getRooms(playerId: string, start: string, end: string): Promise<IHotelRoomBookings> {
    return delay(() => {
      mockHotelStore.reset();
      return {
        roomTypes: [
          {
            roomType: {
              code: "KR",
              name: "Standard King Room",
              guestLimit: 2,
              image: "https://uxmstagingmediacdn.azureedge.net/hotel/KingRoom/KingRoom_Thrillist-1_Resized640x426.jpg",
            },
            perNightCompValue: "$450.00",
            perNightValue: "$899.00",
            bookingId: "85ca19d5-00fd-452b-8a01-f92c76ee61f7",
            available: true,
          },
          {
            roomType: {
              code: "QQ",
              name: "Standard Queen Room",
              guestLimit: 4,
              image:
                "https://uxmstagingmediacdn.azureedge.net/hotel/QueenRoom/DoubleQueenRoom_NightOwl-1_Resized640x426.jpg",
            },
            perNightCompValue: "$500.00",
            perNightValue: "$949.00",
            bookingId: "05ed40ec-bbcf-4f3e-8c0d-1866620c8de6",
            available: true,
          },
        ],
        offerText: getRandom(["OFFER17", null, "OFFER19", "OFFER1"]),
        preferences: getRandomArray(0, 5)
          .fill(1)
          .map(() =>
            getRandom([
              "High Level Floor",
              "Pool View",
              "Near the Elevator",
              "King Bed",
              "Design Adventurer",
              "Balcony",
              "Kitchenette",
              "Tub in Room",
              "Early Check-in",
            ])
          ),
      };
    });
  }

  getSummary(bookingId: string): Promise<IHotelBookingSummary> {
    return delay(() => {
      const summary = mockHotelStore.summary;
      return summary;
    });
  }

  getShift4Info(bookingId: string): Promise<IShift4Info> {
    return delay(() => {
      return {
        accessBlock:
          "A0000,8DD3D28AC0813D6ADEECE5F9C78D9CABC6F314B2AA4C2109B506BD20D4187774A0B30A0B5218A59C0179828871ECAE50C799DB38D0A01FA7B20BFE34F7E6A86209D245F7DB69BBAC6E1AE630AD52695CB44CF5CC8107AA8BA3B165CA9AB969E9FA8530C3050A5AC4BA40851FD4E62E163B362139D518FEC898119D6BB1F6483BFED89E45EFD07BD0A3A4030418AA1A91BF4946F46AC6405C57BAE8F31C5D80CA041A1F605A5C263FBCAE4B05E5F05C49003C941D2DDF9DB47A63A9D06D0012942F9030F63194DA56AB1E1F2632086D86D4AC55389CD718B9010AD32337340FC2F799BB5066DAC200912095A6575B74B8",
        serverUrl: "https://i4go-payment.shift4test.com",
        amountInCents: 25000, // Unused by frontend
      };
    });
  }

  addGuestToBooking(bookingId: string, guest: IHotelBookingGuest): Promise<IHotelBookingGuest> {
    return delay(() => {
      const newGuest = { ...guest, guestId: uuidv4() };
      mockHotelStore.guests.push(newGuest);
      return newGuest;
    });
  }
  removeGuestFromBooking(bookingId: string, guestId: string): Promise<Response> {
    return delay(() => {
      mockHotelStore.guests = mockHotelStore.guests.filter((g) => g.guestId !== guestId);
      return new Response();
    });
  }
  editGuestOnBooking(bookingId: string, guest: IHotelBookingGuest): Promise<IHotelBookingGuest> {
    return delay(() => {
      mockHotelStore.guests = mockHotelStore.guests.map((g) => {
        if (g.guestId === guest.guestId) {
          return guest;
        }
        return g;
      });

      return guest;
    });
  }
  getTrips(playerId: string): IAbortablePromise<IPlayerTrip[]> {
    return abortableDelay(() => {
      return [];
    });
  }
  getComps(playerId: string): IAbortablePromise<IPlayerComp[]> {
    return abortableDelay(() => {
      return [];
    });
  }
  getOffers(playerId: string, categoryIds: number[], statuses: string[]): IAbortablePromise<IPlayerOffer[]> {
    return abortableDelay(() => {
      return mockPlayerDataStore.offers;
    });
  }
  getCreditTransactions(playerId: string): IAbortablePromise<IPlayerCreditTransaction[]> {
    return abortableDelay(() => {
      return [];
    });
  }

  getNotes(playerId: string): IAbortablePromise<IBasicPlayerNote[]> {
    return abortableDelay(() => {
      return mockPlayerDataStore.notes;
    });
  }

  getNote(noteId: number): Promise<IPlayerNote> {
    return delay(() => {
      return mockPlayerDataStore.getNote(noteId);
    });
  }

  addNote(note: ICreateNote): Promise<IPlayerNote> {
    return delay(() => {
      return mockPlayerDataStore.addNote(note);
    });
  }

  editNote(note: IPlayerNote): Promise<IPlayerNote> {
    return delay(() => {
      return mockPlayerDataStore.editNote(note);
    });
  }

  updateSourceOfFunds(playerId: string, sourceOfFunds: IPlayerSourceOfFunds): Promise<Response> {
    return delay(() => {
      return new Response();
    });
  }

  updateBookingPayment(bookingId: string, payment: IHotelBookingPayment): Promise<Response> {
    throw new Error("Method not implemented.");
  }

  updateBookingPaymentRequired(bookingId: string, required: boolean): Promise<Response> {
    return delay(() => {
      return new Response();
    });
  }

  updateBookingPreferences(bookingId: string, codes: string[]): Promise<Response> {
    return delay(() => {
      mockHotelStore.preferences = mockHotelStore.allPreferences.filter((p) => codes.includes(p.code));
      return new Response();
    });
  }

  updateBookingNotes(bookingId: string, note: string): Promise<Response> {
    return delay(() => {
      mockHotelStore.note = note;
      return new Response();
    });
  }

  updateEstimatedArrivalTime(playerId: string, bookingId: string, time: string): Promise<Response> {
    return delay(() => {
      mockHotelStore.estimatedTime = time;
      return new Response();
    });
  }

  bookHotelBooking(bookingId: string): Promise<Response> {
    throw new Error("Method not implemented.");
  }

  getBookingStatus(bookingId: string): Promise<TaskStatus> {
    throw new Error("Method not implemented.");
  }

  getReservationsByStatus(
    playerId: string | number,
    status: "current" | "upcoming" | "cancelled" | "previous"
  ): IAbortablePromise<IBasicReservation[]> {
    return abortableDelay(() => {
      return mockReservations.getByStatus(status);
    });
  }

  getReservation(playerId: string | number, confirmationNumber: string): Promise<IReservation> {
    return delay(() => {
      const reservation = mockReservations.getBy(confirmationNumber);
      if (!reservation) {
        throw new ApiError(-1, "Mock Data Error");
      }
      randomErrorOccurence();
      return reservation;
    });
  }

  cancelReservation(confirmationNumber: string | number): Promise<Response> {
    return delay(() => {
      mockReservations.cancelReservation(confirmationNumber);
      return new Response();
    });
  }

  getWinLossYears(playerId: string | number): Promise<number[]> {
    return delay(() => {
      // NOTE: used to test errors
      if (Math.random() < 0.4) {
        throw new ApiError(-1, "Error getting win loss years");
      }

      return mockPlayerDataStore.getWinLossYears(playerId);
    });
  }

  getWinLossFor(playerId: string | number, winLossYear: string | number): Promise<Blob> {
    return delay(() => {
      // NOTE: used to test errors
      if (Math.random() < 0.4) {
        throw new ApiError(-1, "Error getting win loss PDF");
      }

      return mockPlayerDataStore.getWinLoss(winLossYear);
    });
  }

  unlinkUser(playerId: string | number): Promise<Response> {
    return delay(() => {
      // NOTE: used to test errors
      if (Math.random() < 0.4) {
        throw new ApiError(-1, "Error unlinking user");
      }

      let appInfo = mockPlayerDataStore.getClubInfo(playerId);
      appInfo.lastLinkDate = new Date(0);
      appInfo.initialLinkDate = new Date(0);

      return new Response();
    });
  }

  resetPassword(playerId: string | number): Promise<Response> {
    return delay(() => {
      // NOTE: used to test errors
      if (Math.random() < 0.4) {
        throw new ApiError(-1, "Error resetting password");
      }

      return new Response();
    });
  }

  updateEmail(playerId: string | number): Promise<Response> {
    return delay(() => {
      // NOTE: used to test errors
      if (Math.random() < 0.4) {
        throw new ApiError(-1, "Error updating password");
      }

      return new Response();
    });
  }

  getMobileAppInformation(playerId: string | number): Promise<IPlayerMobileAppInformation> {
    return delay(() => {
      // NOTE: used to test errors
      if (Math.random() < 0.4) {
        throw new ApiError(-1, "Error getting mobile app information");
      }
      // throw new ApiError(404, "player not found");

      return mockPlayerDataStore.getMobileAppInformation(playerId);
    });
  }

  getClubInfo(playerId: string | number): Promise<IClubSerranoInfo> {
    return delay(() => {
      // NOTE: used to test errors
      if (Math.random() < 0.4) {
        throw new ApiError(-1, "Error getting serrano club info");
      }
      // throw new ApiError(404, "player not found");

      return mockPlayerDataStore.getClubInfo(playerId);
    });
  }
}
